import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons'
import {
  Layout,
  Progress,
  Typography,
  Table,
  Space,
  Popconfirm,
  Button,
  Row
} from 'antd'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import { useParams } from 'react-router-dom'
import {
  compareDate,
  compareNumber,
  compareText,
  formatBytes
} from '~/utils/helpers'
import { useCourseQuery, useCaseQuery } from './query'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { PageHeader } from '~/components/PageHeader'
import { LocalDateTime } from '~/components/LocalDateTime'
import { CaseDownloadButton } from '~/components/CaseDownloadButton'
import { userApi } from '~/api'

const { Title, Text } = Typography
const { Content } = Layout

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out'
}

const focusedStyle = {
  borderColor: '#2196f3'
}

const acceptStyle = {
  borderColor: '#00e676'
}

const rejectStyle = {
  borderColor: '#ff1744'
}

const UploadFiles = ({
  pendingFileList = [],
  processingFile = {},
  uploadedFileList = [],
  setFilePending = () => {},
  uploadFiles = () => {},
  removeFileUploaded = () => {}
}) => {
  const { id } = useParams()
  const { data } = useCourseQuery(id)

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!data) {
        return
      }
      await setFilePending({ files: acceptedFiles, data })
      await uploadFiles()
    },
    [data, setFilePending, uploadFiles]
  )

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      onDrop,
      accept: '.zip,.rar,.7z,.iso'
    })

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isFocused, isDragAccept, isDragReject]
  )

  const renderFileUploaded = useMemo(() => {
    if (uploadedFileList && uploadedFileList.length > 0) {
      return uploadedFileList.map((info, idx) => (
        <div key={idx}>
          {info.isLoading && (
            <LoadingOutlined style={{ fontSize: 18, marginRight: 5 }} spin />
          )}

          {info.status === 'exception'
            ? (
            <>
              <Text>
                {info.file.path} - {formatBytes(info.file.size)}{' '}
                <Text type='danger' italic>
                  ( {info.reason} )
                </Text>
              </Text>
              <Progress percent={100} status='exception' />
            </>
              )
            : (
            <>
              <Text>
                {info.file.path} - {formatBytes(info.file.size)}
              </Text>
              <Progress percent={100} />
            </>
              )}
        </div>
      ))
    }
    return null
  }, [uploadedFileList])

  const renderFileProssesing = useMemo(() => {
    if (processingFile && processingFile.file) {
      return (
        <div key={processingFile.file.path}>
          <LoadingOutlined style={{ fontSize: 18, marginRight: 5 }} spin />
          <Text>
            {processingFile.file.path} - {formatBytes(processingFile.file.size)}
          </Text>
          <Progress percent={Math.round(processingFile.progressValue * 100)} />
        </div>
      )
    }
    return null
  }, [processingFile])

  const renderFilePending = useMemo(() => {
    if (pendingFileList && pendingFileList.length > 0) {
      return pendingFileList.map((info, idx) => (
        <div key={idx}>
          <LoadingOutlined style={{ fontSize: 18, marginRight: 5 }} spin />
          <Text>
            {info.path} - {formatBytes(info.size)}
          </Text>
          <Progress percent={0} />
        </div>
      ))
    }
    return null
  }, [pendingFileList])

  const columns = (remove, edit) => [
    {
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => compareNumber(a.id, b.id)
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => compareText(a.name, b.name)
    },
    {
      title: 'Size',
      render: (cases) => formatBytes(cases.size),
      sorter: (a, b) => compareNumber(a.size, b.size)
    },
    {
      title: 'Created At',
      render: (cases) => <LocalDateTime date={cases.created_at} />,
      sorter: (a, b) => compareDate(a.created_at, b.created_at)
    },
    {
      title: 'Uploader',
      render: (cases) => cases.user?.name
    },
    // {
    //   title: 'Require Delete',
    //   align: 'center',
    //   render: (cases) => (
    //     <Checkbox
    //       defaultChecked={cases.require_delete}
    //       onClick={(e) => edit({ caseId: cases.id, checked: e.target.checked })}
    //     ></Checkbox>
    //   ),
    // },
    {
      title: 'Action',
      render(item) {
        return (
          <Space size='middle'>
            <CaseDownloadButton courseId={id} caseId={item.id} />
            <Popconfirm
              title={`Do you want to delete file ${item.name}?`}
              onConfirm={() => remove(item)}
              okText='YES'
            >
              <Button
                type='primary'
                danger
                htmlType='button'
                aria-label='Delete'
                title='Delete'
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Space>
        )
      }
    }
  ]

  const queryClient = useQueryClient()
  useEffect(() => {
    if (uploadedFileList.length > 0) {
      queryClient.invalidateQueries({ queryKey: ['cases', id] })
    }
  }, [id, queryClient, uploadedFileList.length])

  const { mutate: removeCase } = useMutation(
    ({ id: caseId, name }) => userApi.removeCase(id, caseId),
    {
      onSuccess: (_, { name }) => {
        removeFileUploaded(name)
        queryClient.invalidateQueries({ queryKey: ['cases', id] })
      }
    }
  )

  const { mutate: editCase } = useMutation(
    ({ caseId, checked }) =>
      userApi.editCase(id, caseId, { require_delete: checked }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['cases', id] })
      }
    }
  )

  const { data: uploadedCases, isLoading } = useCaseQuery(id)
  const dataSource = useTableDataSourceKey(uploadedCases)

  const totalSize = useMemo(() => {
    if (!dataSource) {
      return 0
    }

    const result = dataSource.reduce((carry, item) => {
      return carry + item.size
    }, 0)

    return formatBytes(result)
  }, [dataSource])

  if (!data) {
    return null
  }

  return (
    <>
      <PageHeader
        onBack={() => window.history.back()}
        className='radup-page-header'
        title={'Cases > ' + data?.name}
        extra={[]}
      />

      <Content>
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <p>
            Drag and drop your files here, or click to select file in ISO, Zip,
            7Zip or RAR file format only
          </p>
        </div>
        <div>{renderFileUploaded}</div>
        <div>{renderFileProssesing}</div>
        <div>{renderFilePending}</div>

        <Title level={5} style={{ marginTop: '16px' }}>
          Files ({totalSize})
        </Title>
        <Table
          size='small'
          tableLayout='auto'
          dataSource={dataSource}
          columns={columns(removeCase, editCase)}
          loading={isLoading}
          pagination={false}
          scroll={{ y: 500, x: '100%' }}
          footer={() => (
            <Row justify='start'>
              <span>Total: {dataSource.length} cases</span>
            </Row>
          )}
        />
      </Content>
    </>
  )
}

UploadFiles.propTypes = {
  pendingFileList: PropTypes.array,
  processingFile: PropTypes.object,
  uploadedFileList: PropTypes.array,

  setFilePending: PropTypes.func,
  uploadFiles: PropTypes.func,
  removeFileUploaded: PropTypes.func
}

export default UploadFiles
